// app/guides/beginners-programming.tsx — מדריך תכנות למתחילים | Kursi
import Head from 'expo-router/head';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  Platform,
} from 'react-native';
import { useRouter, Link } from 'expo-router';
import { theme } from '@/constants/theme';

const SITE_URL = 'https://kursiaii.com';

const breadcrumbSchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'דף הבית', item: `${SITE_URL}/` },
    { '@type': 'ListItem', position: 2, name: 'קורסים', item: `${SITE_URL}/courses` },
    { '@type': 'ListItem', position: 3, name: 'תכנות', item: `${SITE_URL}/courses/programming` },
    { '@type': 'ListItem', position: 4, name: 'תכנות למתחילים', item: `${SITE_URL}/guides/beginners-programming` },
  ],
});

const faqSchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'האם אפשר ללמוד תכנות בלי רקע מקדים?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'כן. תכנות הוא אחד התחומים שניתן להיכנס אליו ללא רקע מקדים. קורסים למתחילים מתחילים מאפס ומלמדים את הבסיס. מה שנדרש: סבלנות, סקרנות ויכולת לעשות תרגול עצמאי.',
      },
    },
    {
      '@type': 'Question',
      name: 'כמה זמן לוקח ללמוד תכנות מאפס?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '3–6 חודשים לרמת ג\'וניור (Full Stack בסיסי), 6–12 חודשים לרמה שמאפשרת קבלה לעבודה. זה תלוי בעצימות הלימוד ובסוג הקורס.',
      },
    },
    {
      '@type': 'Question',
      name: 'באיזה שפת תכנות להתחיל?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'למסלול Web — JavaScript. למסלול Data/AI — Python. JavaScript היא הנפוצה ביותר בשוק הישראלי ומאפשרת לבנות אתרים ואפליקציות. Python קלה ללמידה ומשמשת ב-AI, דאטה ואוטומציה.',
      },
    },
    {
      '@type': 'Question',
      name: 'מה ההבדל בין קורס תכנות לבוטקאמפ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'קורס תכנות: לרוב 3–6 חודשים, מתמקד בנושא ספציפי, גמיש יותר. בוטקאמפ: לרוב 4–6 חודשים אינטנסיביים (40 שעות שבוע), מכסה מסלול שלם מאפס עד ג\'וניור, לרוב כולל הכנה לראיונות.',
      },
    },
    {
      '@type': 'Question',
      name: 'האם כדאי ללמוד תכנות אונליין או פרונטלי?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'למתחילים מוחלטים — פרונטלי עדיף בגלל המבנה, האחריות הקבוצתית והמנטורינג הישיר. מי שיכול לנהל זמן ולתרגל עצמאית — יכול להצליח גם באונליין. בשני המקרים: תרגול יומי חיוני.',
      },
    },
  ],
});

export default function BeginnersProgrammingPage() {
  const router = useRouter();

  return (
    <>
      {Platform.OS === 'web' && (
        <Head>
          <title>תכנות למתחילים 2026 — המדריך המלא לבחירת קורס | Kursi</title>
          <meta
            name="description"
            content="מדריך מלא לתכנות למתחילים: באיזה שפה להתחיל, כמה זמן לוקח, מה ההבדל בין קורס לבוטקאמפ, אונליין מול פרונטלי, ורשימת בדיקה לפני רישום."
          />
          <link rel="canonical" href={`${SITE_URL}/guides/beginners-programming`} />
          <meta property="og:url" content={`${SITE_URL}/guides/beginners-programming`} />
          <meta property="og:title" content="תכנות למתחילים 2026 — המדריך המלא | Kursi" />
          <meta
            property="og:description"
            content="מאפס עד ג'וניור: כל מה שצריך לדעת לפני שבוחרים קורס תכנות ראשון. שפה, פורמט, משך, ומה לבדוק לפני רישום."
          />
          <meta property="og:type" content="article" />
          <meta property="og:image" content={`${SITE_URL}/og-image.webp`} />
          <meta name="robots" content="index,follow" />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbSchema }} />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqSchema }} />
        </Head>
      )}

      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        {/* Nav */}
        <View style={styles.navBar}>
          <Link
            href="/courses/programming"
            style={styles.navBtn}
            accessibilityRole="link"
          >
            <Text style={styles.navBtnText}>← קורסי תכנות</Text>
          </Link>
          <Link
            href="/"
            style={[styles.navBtn, styles.navBtnHome]}
            accessibilityRole="link"
          >
            <Text style={[styles.navBtnText, styles.navBtnHomeText]}>דף הבית</Text>
          </Link>
        </View>

        <View style={styles.innerContent}>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>למתחילים</Text>
          </View>

          <Text
            style={styles.h1}
            {...(Platform.OS === 'web' && ({ role: 'heading', 'aria-level': 1 } as any))}
          >
            תכנות למתחילים — המדריך המלא לבחירת קורס ראשון
          </Text>

          <Text style={styles.intro}>
            אין רקע? לא יודעים מאיפה להתחיל? זה המדריך שיעזור לכם לבחור נכון — שפה, פורמט, משך
            ומה לבדוק לפני שמשלמים.
          </Text>

          {/* Section 1 */}
          <View style={styles.section}>
            <Text style={styles.h2}>מה זה אומר "מתחיל בתכנות"?</Text>
            <Text style={styles.body}>
              מתחיל בתכנות הוא כל מי שלא כתב קוד בצורה מסודרת לפני כן. אין דרישת רקע מקדים —
              לא מתמטיקה, לא הנדסה, לא "כישרון מיוחד". מה שנדרש: סבלנות לתרגול וסקרנות
              לפתרון בעיות.
            </Text>
            <View style={styles.bulletList}>
              {[
                'אין צורך ברקע מקדים',
                'מתמטיקה ברמת תיכון מספיקה',
                'מחשב עם חיבור אינטרנט — זה הכל',
                'מחויבות לתרגול יומי חיונית',
              ].map((b, i) => (
                <View key={i} style={styles.bulletRow}>
                  <Text style={styles.bulletDot}>•</Text>
                  <Text style={styles.bulletText}>{b}</Text>
                </View>
              ))}
            </View>
          </View>

          {/* Section 2 */}
          <View style={styles.section}>
            <Text style={styles.h2}>באיזה שפת תכנות להתחיל?</Text>
            <Text style={styles.body}>
              הבחירה תלויה במטרה. אין "שפה טובה יותר" — יש שפה מתאימה למסלול.
            </Text>
            <View style={styles.bulletList}>
              {[
                'Web (אתרים, אפליקציות) → JavaScript / TypeScript',
                'Data Science, AI, אוטומציה → Python',
                'מובייל (iOS/Android) → React Native, Swift, Kotlin',
                'QA ואוטומציית בדיקות → Python, Java',
                'Backend (שרתים, APIs) → Node.js, Python, Java',
              ].map((b, i) => (
                <View key={i} style={styles.bulletRow}>
                  <Text style={styles.bulletDot}>•</Text>
                  <Text style={styles.bulletText}>{b}</Text>
                </View>
              ))}
            </View>
            <Text style={[styles.body, { marginTop: 12 }]}>
              המלצת Kursi למתחילים: JavaScript לרוצים לפתח ממשקים ואתרים, Python לרוצים
              עבודה עם נתונים ו-AI.
            </Text>
          </View>

          {/* Section 3 */}
          <View style={styles.section}>
            <Text style={styles.h2}>כמה זמן לוקח ללמוד תכנות מאפס?</Text>
            <Text style={styles.body}>
              ציר זמן ריאלי לרמת ג'וניור שמוכן לחפש עבודה:
            </Text>
            <View style={styles.bulletList}>
              {[
                '3 חודשים: בסיס של שפה אחת + פרויקט קטן',
                '6 חודשים: Full Stack בסיסי + 2–3 פרויקטים בפורטפוליו',
                '9–12 חודשים: רמת ג\'וניור + הכנה לראיונות + חיפוש עבודה',
                'לימוד בוטקאמפ אינטנסיבי: 4–6 חודשים מרוכזים',
              ].map((b, i) => (
                <View key={i} style={styles.bulletRow}>
                  <Text style={styles.bulletDot}>•</Text>
                  <Text style={styles.bulletText}>{b}</Text>
                </View>
              ))}
            </View>
          </View>

          {/* Section 4 */}
          <View style={styles.section}>
            <Text style={styles.h2}>קורס vs בוטקאמפ — מה ההבדל?</Text>
            <View style={styles.comparisonRow}>
              <View style={[styles.comparisonCard, { backgroundColor: '#FAF5EA' }]}>
                <Text style={styles.comparisonTitle}>קורס תכנות</Text>
                <Text style={styles.comparisonItem}>• 3–6 חודשים</Text>
                <Text style={styles.comparisonItem}>• נושא ספציפי</Text>
                <Text style={styles.comparisonItem}>• גמישות בזמנים</Text>
                <Text style={styles.comparisonItem}>• 3,000–12,000 ₪</Text>
              </View>
              <View style={[styles.comparisonCard, { backgroundColor: '#FEF3C7' }]}>
                <Text style={styles.comparisonTitle}>בוטקאמפ</Text>
                <Text style={styles.comparisonItem}>• 4–6 חודשים אינטנסיביים</Text>
                <Text style={styles.comparisonItem}>• מסלול שלם מאפס</Text>
                <Text style={styles.comparisonItem}>• 40 שעות שבוע</Text>
                <Text style={styles.comparisonItem}>• 14,000–22,000 ₪</Text>
              </View>
            </View>
          </View>

          {/* Checklist */}
          <View style={styles.section}>
            <Text style={styles.h2}>צ'ק-ליסט לפני רישום לקורס תכנות ראשון</Text>
            {[
              'מוגדר מסלול ברור (Web / Data / Mobile / QA)',
              'יש פרויקטים אמיתיים בסילבוס (לא רק הסברים)',
              'יש בדיקת תרגילים ומשוב',
              'המרצה פעיל ומנגיש לשאלות',
              'יש קהילה ותמיכה אחרי הקורס',
              'יש הכוונה לתיק עבודות',
              'מדיניות ביטול/החזר ברורה',
            ].map((item, i) => (
              <View key={i} style={styles.checkRow}>
                <View style={styles.checkbox} />
                <Text style={styles.checkText}>{item}</Text>
              </View>
            ))}
          </View>

          {/* FAQ */}
          <View style={styles.section}>
            <Text style={styles.h2}>שאלות נפוצות</Text>
            {[
              { q: 'האם אפשר ללמוד תכנות בלי רקע?', a: 'כן. קורסים למתחילים מתחילים מאפס. מה שנדרש: סבלנות וסקרנות.' },
              { q: 'כמה זמן לוקח?', a: '6–12 חודשים לרמת ג\'וניור. תלוי בעצימות הלימוד.' },
              { q: 'אונליין או פרונטלי למתחילים?', a: 'פרונטלי עדיף למתחילים מוחלטים — מבנה ומנטורינג עוזרים. אונליין אפשרי למי שיכול לנהל זמן עצמאית.' },
            ].map((item, i) => (
              <View key={i} style={styles.faqItem}>
                <Text style={styles.faqQ}>{item.q}</Text>
                <Text style={styles.faqA}>{item.a}</Text>
              </View>
            ))}
          </View>

          {/* CTA */}
          <View style={styles.ctaBlock}>
            <Text style={styles.ctaTitle}>רוצים לדעת מה מתאים לכם?</Text>
            <Text style={styles.ctaBody}>ה-AI של Kursi ישאל אתכם 3 שאלות ויחזיר המלצה ספציפית לרמה ולמטרה שלכם.</Text>
            <Pressable
              style={({ pressed }) => [styles.ctaBtn, pressed && { opacity: 0.85 }]}
              onPress={() => router.push('/chat')}
              accessibilityRole="button"
            >
              <Text style={styles.ctaBtnText}>מצאו קורס תכנות עכשיו</Text>
            </Pressable>
          </View>

          {/* Internal links */}
          <View style={styles.linksSection}>
            <Text style={styles.linksTitle}>קישורים רלוונטיים</Text>
            <View style={styles.linksGrid}>
              {[
                { label: 'כל קורסי התכנות', route: '/courses/programming' },
                { label: 'קורסי בינה מלאכותית', route: '/courses/ai' },
                { label: 'קורסי דאטה', route: '/courses/data-ai' },
                { label: 'קורס אונליין vs פרונטלי', route: '/compare/online-vs-frontal' },
                { label: 'בחירת קורס לפי תקציב', route: '/guides/by-budget' },
              ].map((link) => (
                <Pressable
                  key={link.route}
                  style={({ pressed }) => [styles.linkItem, pressed && { opacity: 0.75 }]}
                  onPress={() => router.push(link.route as any)}
                  accessibilityRole="link"
                >
                  <Text style={styles.linkText}>{link.label} →</Text>
                </Pressable>
              ))}
            </View>
          </View>
        </View>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F2EBDD' },
  content: { paddingBottom: 56 },
  innerContent: { maxWidth: 820, alignSelf: 'center' as any, width: '100%', paddingHorizontal: 16 },
  navBar: {
    flexDirection: 'row' as any, justifyContent: 'space-between' as any,
    alignItems: 'center' as any, paddingHorizontal: 16, paddingVertical: 12, gap: 10,
  },
  navBtn: { paddingVertical: 8, paddingHorizontal: 14, borderRadius: 0, backgroundColor: '#FAF5EA' },
  navBtnHome: { backgroundColor: '#FAF5EA' },
  navBtnPressed: { opacity: 0.7 },
  navBtnText: { fontSize: 14, fontFamily: 'Heebo-Medium', color: theme.colors.primary },
  navBtnHomeText: { color: '#7A6F5F' },
  badge: {
    backgroundColor: 'transparent', borderWidth: 1,
    borderColor: '#1A1611', borderRadius: 0,
    paddingHorizontal: 12, paddingVertical: 5, marginBottom: 10, alignSelf: 'flex-end' as any,
    marginTop: 16,
  },
  badgeText: { fontSize: 12, fontFamily: 'Heebo-Bold', color: theme.colors.primary },
  h1: {
    fontSize: Platform.OS === 'web' ? 32 : 24, fontFamily: 'Heebo-Bold', color: '#1A1611',
    textAlign: 'right' as any, marginBottom: 12, lineHeight: Platform.OS === 'web' ? 44 : 34,
  },
  intro: {
    fontSize: 16, fontFamily: 'Heebo-Regular', color: '#7A6F5F',
    textAlign: 'right' as any, lineHeight: 26, marginBottom: 20,
  },
  section: {
    backgroundColor: '#FAF5EA', borderRadius: 0, borderWidth: 1,
    borderColor: '#1A1611', padding: 20, marginBottom: 14,
  },
  h2: {
    fontSize: 18, fontFamily: 'Heebo-Bold', color: '#1A1611',
    textAlign: 'right' as any, marginBottom: 10, lineHeight: 28,
  },
  body: {
    fontSize: 15, fontFamily: 'Heebo-Regular', color: '#3A332A',
    textAlign: 'right' as any, lineHeight: 24, marginBottom: 10,
  },
  bulletList: { gap: 8 },
  bulletRow: { flexDirection: 'row-reverse' as any, gap: 8, alignItems: 'flex-start' as any },
  bulletDot: { fontSize: 14, color: theme.colors.primary, fontFamily: 'Heebo-Bold', lineHeight: 22 },
  bulletText: { flex: 1, fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'right' as any, lineHeight: 22 },
  comparisonRow: { flexDirection: 'row-reverse' as any, gap: 12 },
  comparisonCard: { flex: 1, borderRadius: 0, padding: 14, gap: 6 },
  comparisonTitle: { fontSize: 15, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'center' as any, marginBottom: 6 },
  comparisonItem: { fontSize: 13, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'right' as any },
  checkRow: { flexDirection: 'row-reverse' as any, gap: 10, marginBottom: 10, alignItems: 'center' as any },
  checkbox: { width: 18, height: 18, borderRadius: 4, borderWidth: 2, borderColor: theme.colors.primary },
  checkText: { flex: 1, fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'right' as any, lineHeight: 22 },
  faqItem: { marginBottom: 14 },
  faqQ: { fontSize: 15, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any, marginBottom: 6 },
  faqA: { fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'right' as any, lineHeight: 22 },
  ctaBlock: { backgroundColor: '#FAF5EA', borderRadius: 0, padding: 24, alignItems: 'center' as any, marginTop: 8, marginBottom: 20 },
  ctaTitle: { fontSize: 18, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'center' as any, marginBottom: 8 },
  ctaBody: { fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'center' as any, marginBottom: 16, lineHeight: 22 },
  ctaBtn: { backgroundColor: theme.colors.primary, paddingVertical: 12, paddingHorizontal: 28, borderRadius: 0 },
  ctaBtnText: { fontSize: 15, fontFamily: 'Heebo-Bold', color: '#FAF5EA' },
  linksSection: { marginBottom: 16 },
  linksTitle: { fontSize: 17, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any, marginBottom: 12 },
  linksGrid: { gap: 8 },
  linkItem: {
    paddingVertical: 12, paddingHorizontal: 16, backgroundColor: '#FAF5EA',
    borderRadius: 0, borderWidth: 1, borderColor: '#1A1611',
  },
  linkText: { fontSize: 14, fontFamily: 'Heebo-Medium', color: theme.colors.primary, textAlign: 'right' as any },
});
